import MuleBadge from '../../components/MuleBadge';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronRightIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import userSettingsService from '../../services/userSettings.service';
import TagBadge, { TagColor } from '../../components/TagBadge';
import {
  PLANNING_QUALIFICATIONS,
  getQualificationColor,
  hasAllQualifications,
  parseQualifications,
  qualificationOptions,
} from '../../constants/driverQualifications';
import ParkingLocationModal, { getParkingColorStyles } from '../../components/ParkingLocationModal';
import { Tooltip } from '../../components/ui/Tooltip';
import MultiSelect from '../../components/MultiSelect';
import { useWorkingStatusPreference } from '../../contexts/WorkingStatusPreferenceContext';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import DriverPhoneLink from '../../components/DriverPhoneLink';
import { isRealDriver } from '../../utils/driverFilters';
import { toLocalDateKey } from '../../utils/dateUtils';
import { matchesDriverPreference } from '../../utils/driverPreferenceFilter';
import {
  WORKING_CATEGORIES,
  getWorkingCategoryLabel,
  WORKING_CATEGORY_LIVE_DISPATCH,
  WORKING_CATEGORY_GROUNDED_YARD,
  WORKING_CATEGORY_DONE_FOR_DAY,
} from '../../constants/workingCategories';

interface LocationPreference {
  location_id: number;
  location_name: string;
  location_type?: string;
  location_address1?: string;
  location_address2?: string;
  location_city?: string;
  location_state?: string;
  location_zip?: string;
  preference_type: 'preferred' | 'not_preferred' | 'banned';
  ban_start_date?: string;
  ban_end_date?: string;
}

interface DeliveryCount {
  location_name: string;
  count: number;
  is_return: boolean;
  is_live: boolean;
  appointment_time?: string;
  leg_action?: string;
}

interface DriverRejection {
  pronumber: string;
  origcode: string;
  destcode: string;
  orig_name: string;
  dest_name: string;
  rejectreason: string;
  rejectcomment: string | null;
}

interface DriverPlanningData {
  driver: string;
  driver_name: string;
  isMule?: boolean;
  preference: string;  // OTR or LOCAL
  parkingloc: number | null;
  parking_location_name: string;
  parking_color: number | null;
  region: string;
  endorsements: string | null;
  start_time: string | null;
  live_pref: string | null;
  driver_status: string | null;
  working_category?: string | null;
  status_comments?: string | null;
  oos_comments?: string | null;
  oos_type?: string | null;
  permits_requested?: boolean;
  port_appointment_made?: boolean;
  confirmed?: boolean;
  preferred: LocationPreference[];
  not_preferred: LocationPreference[];
  banned: LocationPreference[];
  delivery_counts: DeliveryCount[];
  est_pay: number | null;
  est_miles: number | null;
  textmsgnumber?: string;
  homephone?: string;
}

interface DispatcherGroup {
  dispatcher: string;
  region: string;
  drivers: DriverPlanningData[];
}

interface DriverPlanningProps {
  selectedDispatchers: string[];
  selectedRegions: string[];
  selectedDriverType: string[];
  selectedDate: Date | null;
  refreshTrigger: number;
  groupByDispatcher: boolean;
  showAppointmentTimes: boolean;
  usernameToDisplayname: Map<string, string>;
  /** Opens the Drivers page's Driver Legs drawer, the one Week View opens. */
  onOpenLegsDrawer: (driver: string, driverName: string, dateKey: string) => void;
}

type SortDirection = 'asc' | 'desc' | null;
type SortColumn = 'dispatcher' | 'driver' | 'endorsements' | 'driver_status' | 'start_time' | 'live_pref' | 'preferred' | 'not_preferred' | 'banned' | 'delivery_counts' | 'est_pay' | 'est_miles';

interface SortState {
  column: SortColumn | null;
  direction: SortDirection;
}

interface FlatDriverData extends DriverPlanningData {
  dispatcher: string;
  dispatcher_region: string;
}

// Convert name to title case (camel case)
const toTitleCase = (str: string | undefined) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format time from 24-hour (HH:MM) to 12-hour with AM/PM
const formatTime12Hour = (time: string | null) => {
  if (!time) return '';
  const [hoursStr, minutesStr] = time.split(':');
  const hours = parseInt(hoursStr);
  const minutes = parseInt(minutesStr);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutesStr} ${period}`;
};

const formatBanPeriod = (ban: LocationPreference) => {
  if (!ban.ban_end_date) {
    return 'Permaban';
  }

  const end = new Date(ban.ban_end_date);
  return `${end.getMonth() + 1}/${end.getDate()}/${end.getFullYear()}`;
};

// Port terminals that require permits and port appointments
const PORT_TERMINALS = ['Mobile', 'Savannah', 'Charleston', 'Baltimore'];

// Check if a driver's region is a port terminal
const isPortTerminal = (region: string | undefined) => {
  if (!region) return false;
  return PORT_TERMINALS.some(terminal => region.toLowerCase().includes(terminal.toLowerCase()));
};

// Sort drivers within a group by saved order, with unsaved drivers at the end
const sortDriversByOrder = (
  drivers: DriverPlanningData[],
  savedOrder: string[] | undefined
): DriverPlanningData[] => {
  if (!savedOrder || savedOrder.length === 0) {
    return drivers;
  }

  const orderMap = new Map(savedOrder.map((id, index) => [id, index]));

  return [...drivers].sort((a, b) => {
    const aIndex = orderMap.get(a.driver);
    const bIndex = orderMap.get(b.driver);

    // Both have saved positions - sort by saved order
    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }
    // Only a has saved position - a comes first
    if (aIndex !== undefined) return -1;
    // Only b has saved position - b comes first
    if (bIndex !== undefined) return 1;
    // Neither has saved position - maintain original order
    return 0;
  });
};

interface StatusStyleInfo {
  permits_requested?: boolean;
  port_appointment_made?: boolean;
  confirmed?: boolean;
  working_category?: string | null;
}

// Whether a Planned / Accepted cell prints the working category under the
// status, like the Working/Not Working tab always does. Grounded Yard, Do Not
// Preplan and Done For The Day are always labeled — their purple/orange/sky
// fill needs the words to read — the rest follow the "extended working
// status" preference.
export const showsPlannedCategoryLabel = (
  workingCategory: string | null | undefined,
  showExtendedWorkingStatus: boolean
): boolean => {
  if (!workingCategory) return false;
  if (
    workingCategory === WORKING_CATEGORY_GROUNDED_YARD ||
    workingCategory === WORKING_CATEGORY_LIVE_DISPATCH ||
    workingCategory === WORKING_CATEGORY_DONE_FOR_DAY
  ) {
    return true;
  }
  return showExtendedWorkingStatus;
};

export const getStatusStyle = (status: string | null | undefined, statusInfo?: StatusStyleInfo, driverRegion?: string) => {
  if (!status) return '';

  // Handle (OOS) suffix statuses
  if (status.includes('(OOS)')) {
    return 'bg-green-600 text-white';
  }

  switch (status) {
    case 'Planned':
    case 'Planned Accepted':
      // Grounded Yard / Do Not Preplan / Done For The Day keep their category
      // color on planned days too — confirmed or not, permits or not
      // (2026-09-08) — matching the Working/Not Working tab. Done For The Day
      // matters most here: a finished driver nearly always has legs, so the
      // planning views show him as Planned / Accepted, never Available.
      // In Route OTR keeps the plain palette.
      if (statusInfo?.working_category === WORKING_CATEGORY_LIVE_DISPATCH) {
        return 'bg-orange-100 text-orange-700';
      }
      if (statusInfo?.working_category === WORKING_CATEGORY_GROUNDED_YARD) {
        return 'bg-purple-100 text-purple-700';
      }
      if (statusInfo?.working_category === WORKING_CATEGORY_DONE_FOR_DAY) {
        return 'bg-sky-300 text-sky-900';
      }
      // Check if this is a port terminal driver who is missing permits or appointment
      if (statusInfo && isPortTerminal(driverRegion)) {
        const permitsRequested = statusInfo.permits_requested === true;
        const hasAppointment = statusInfo.port_appointment_made === true;
        if (!permitsRequested || !hasAppointment) {
          return 'bg-yellow-400 text-yellow-900'; // Yellow for incomplete port requirements
        }
      }
      // Check if not confirmed
      if (statusInfo && !statusInfo.confirmed) {
        return 'bg-yellow-400 text-yellow-900'; // Yellow for unconfirmed drivers
      }
      return 'bg-green-600 text-white';
    case 'Available':
    case 'WORKING':
      // Category color outranks the permit/unconfirmed yellows — those
      // warnings are near-universal in real data and made these colors
      // unreachable. "Do Not Preplan" is stored as 'Live Dispatch' — see
      // workingCategories.ts
      if (statusInfo?.working_category === WORKING_CATEGORY_LIVE_DISPATCH) {
        return 'bg-orange-100 text-orange-700';
      }
      if (statusInfo?.working_category === WORKING_CATEGORY_GROUNDED_YARD) {
        return 'bg-purple-100 text-purple-700';
      }
      // Done For The Day: worked today, taking no more work. Sky blue on
      // purpose — NOT the Not Working blue (bg-info = blue-100), so a finished
      // driver never reads as one who never worked.
      if (statusInfo?.working_category === WORKING_CATEGORY_DONE_FOR_DAY) {
        return 'bg-sky-300 text-sky-900';
      }
      // Check if this is a port terminal driver who is missing permits or appointment
      if (statusInfo && isPortTerminal(driverRegion)) {
        const permitsRequested = statusInfo.permits_requested === true;
        const hasAppointment = statusInfo.port_appointment_made === true;
        if (!permitsRequested || !hasAppointment) {
          return 'bg-flag text-flag-fg'; // Yellow for incomplete port requirements
        }
      }
      // Check if not confirmed
      if (statusInfo && !statusInfo.confirmed) {
        return 'bg-flag text-flag-fg'; // Yellow for unconfirmed drivers
      }
      return 'bg-green-100 text-green-700';
    case 'Out of Service':
    case 'Out Of Service':
      return 'bg-danger text-danger-fg';
    case 'Pending Return':
      return 'bg-red-50 text-red-700';
    case 'Not Working':
    case 'NOT WORKING':
      return 'bg-info text-info-fg';
    case 'Unknown':
      return 'text-gray-400';
    default:
      return '';
  }
};

const getDisplayStatus = (status: string | null | undefined) => {
  if (!status) return status;
  if (status === 'WORKING') return 'Available';
  if (status === 'NOT WORKING') return 'Not Working';
  if (status === 'Out Of Service' || status === 'Out of Service') return 'OOS';
  if (status === 'Planned Accepted (OOS)') return 'Accepted (OOS)';
  if (status === 'Planned (OOS)') return 'Planned (OOS)';
  if (status === 'Planned Accepted') return 'Accepted';
  if (status === 'Planned') return 'Planned';
  return status;
};

const DriverPlanning: React.FC<DriverPlanningProps> = ({
  selectedDispatchers,
  selectedRegions,
  selectedDriverType,
  selectedDate,
  refreshTrigger,
  groupByDispatcher,
  showAppointmentTimes,
  usernameToDisplayname,
  onOpenLegsDrawer,
}) => {
  const { showExtendedWorkingStatus, planningShowAllMoves, planningShowTwic } = useWorkingStatusPreference();
  const { showColumnWidths, compactTableMode } = useDisplayPreferences();

  // TWIC is opt-out via the Quals toolbar toggle: roughly three in four drivers
  // carry a valid one, so left on it dominates the column. Only the badge is
  // suppressed -- TWIC stays selectable in the Quals filter either way.
  const showQual = (qual: string) => planningShowTwic || qual !== 'TWIC';
  const [data, setData] = useState<DispatcherGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectionsByDriver, setRejectionsByDriver] = useState<Record<string, DriverRejection[]>>({});
  const [expandedDispatchers, setExpandedDispatchers] = useState<Set<string>>(new Set());
  // Ref, NOT state: as state (and a dep of the fetch effect) the flag's flip
  // after the first load re-ran the effect, so every page load fetched twice —
  // the second pass masquerading as a "background refresh".
  const initialLoadCompleteRef = useRef(false);
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: null });
  const [columnSearches, setColumnSearches] = useState({
    dispatcher: '',
    driver: '',
    startTime: '',
    preferred: '',
    notPreferred: '',
    banned: '',
    deliveryCounts: '',
    rejections: '',
    estPay: '',
    estMiles: ''
  });
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [livePrefFilter, setLivePrefFilter] = useState<string[]>([]);
  const [endorsementsFilter, setEndorsementsFilter] = useState<string[]>([]);

  // Driver sort order (from Drivers page drag-and-drop)
  const [driverSortOrder, setDriverSortOrder] = useState<Record<string, string[]>>({});

  // Load driver sort order from user settings
  useEffect(() => {
    const loadSortOrder = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('drivers_sort_order');
        if (setting?.setting_value) {
          setDriverSortOrder(setting.setting_value);
        }
      } catch (error) {
        console.error('Failed to load driver sort order:', error);
      }
    };
    loadSortOrder();
  }, []);

  // Parking location modal state
  const [parkingModalOpen, setParkingModalOpen] = useState(false);
  const [parkingModalDriver, setParkingModalDriver] = useState('');
  const [parkingModalDriverName, setParkingModalDriverName] = useState('');
  const [parkingModalCurrentLoc, setParkingModalCurrentLoc] = useState<number | null>(null);
  const [parkingModalCurrentName, setParkingModalCurrentName] = useState('');
  const [parkingModalCurrentColor, setParkingModalCurrentColor] = useState<number | null>(null);

  // Status filter options (matching Working/Not Working tab)
  const statusOptions = [
    { value: 'Available', label: 'Available' },
    ...WORKING_CATEGORIES.map(c => ({ value: c.value, label: c.label })),
    { value: 'Not Working', label: 'Not Working' },
    { value: 'Out Of Service', label: 'OOS' },
    { value: 'Unknown', label: 'Unknown' },
    { value: 'Planned', label: 'Planned' },
    { value: 'Planned Accepted', label: 'Accepted' },
  ];

  // Live Loads filter options
  const livePrefOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'neutral', label: 'No Pref' },
  ];

  // Considerations filter options — PPE rides the same string on this tab only.
  const endorsementsOptions = qualificationOptions(PLANNING_QUALIFICATIONS);

  const toggleDispatcher = (dispatcher: string) => {
    setExpandedDispatchers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dispatcher)) {
        newSet.delete(dispatcher);
      } else {
        newSet.add(dispatcher);
      }
      return newSet;
    });
  };

  const openParkingModal = (driver: DriverPlanningData) => {
    setParkingModalDriver(driver.driver);
    setParkingModalDriverName(driver.driver_name);
    setParkingModalCurrentLoc(driver.parkingloc);
    setParkingModalCurrentName(driver.parking_location_name);
    setParkingModalCurrentColor(driver.parking_color);
    setParkingModalOpen(true);
  };

  const handleParkingLocationSave = async () => {
    if (!selectedDate) return;
    try {
      const response = await api.get('/safety/driver-planning', {
        params: { date: selectedDate.toISOString().split('T')[0], show_all_moves: planningShowAllMoves }
      });
      setData(response.data || []);
    } catch (error) {
      console.error('Error refreshing driver planning:', error);
    }
  };

  // Get badge color and tooltip based on leg action type
  const getLegBadgeInfo = (delivery: DeliveryCount): { color: TagColor; tooltip: string; suffix: string } => {
    const action = (delivery.leg_action || 'DELIVERY').toUpperCase();
    if (delivery.is_return) return { color: 'yellow', tooltip: 'Return', suffix: '' };
    switch (action) {
      case 'DELIVERY':
        return delivery.is_live
          ? { color: 'purple', tooltip: 'Live Delivery', suffix: ' (L)' }
          : { color: 'blue', tooltip: 'Drop Delivery', suffix: '' };
      case 'PICKUP':
        return { color: 'green', tooltip: 'Pickup', suffix: ' (P)' };
      case 'STOPOFF':
        return { color: 'orange', tooltip: 'Stopoff', suffix: ' (S)' };
      case 'GROUNDED':
        return { color: 'red', tooltip: 'Grounded', suffix: ' (G)' };
      default:
        return { color: 'gray', tooltip: action.charAt(0) + action.slice(1).toLowerCase(), suffix: '' };
    }
  };

  // A delivery opens the Driver Legs drawer for the day on screen, exactly as a
  // Week View day cell does: the whole cell is the target, and the drawer is the
  // one Drivers.tsx owns. It used to open search results for every current load
  // the driver had, whatever the day.
  const openDayLegs = (driver: DriverPlanningData) => {
    if (selectedDate) onOpenLegsDrawer(driver.driver, driver.driver_name, toLocalDateKey(selectedDate));
  };

  const renderDeliveryCell = (driver: DriverPlanningData) => (
    <td
      className="px-1 py-0 text-xs border border-rule align-top cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
      role="button"
      tabIndex={0}
      aria-label={`View legs for ${driver.driver_name} (${driver.driver})`}
      onClick={() => openDayLegs(driver)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDayLegs(driver);
        }
      }}
    >
      <div className="flex flex-wrap gap-0.5">
        {driver.delivery_counts?.map((delivery, idx) => {
          const { color, tooltip: tooltipText, suffix: labelSuffix } = getLegBadgeInfo(delivery);

          let label: string;
          if (showAppointmentTimes && delivery.appointment_time) {
            label = `${delivery.location_name} ${delivery.appointment_time}${labelSuffix}`;
          } else if (showAppointmentTimes && !delivery.appointment_time) {
            label = `${delivery.location_name}${labelSuffix}`;
          } else {
            label = `${delivery.location_name} x${delivery.count}${labelSuffix}`;
          }

          return (
            <Tooltip key={idx} content={tooltipText}>
              <TagBadge
                label={label}
                color={color}
                className="whitespace-nowrap text-[0.625rem]"
              />
            </Tooltip>
          );
        })}
      </div>
    </td>
  );

  // Filter data based on selected dispatchers, regions, search, and column searches
  const filteredData = useMemo(() => {
    // First, exclude placeholder/admin drivers from all groups
    let filtered = data.map(group => ({
      ...group,
      drivers: group.drivers.filter(driver => isRealDriver(driver.driver))
    })).filter(group => group.drivers.length > 0);

    if (selectedDispatchers.length > 0) {
      const selectedDisplaynames = new Set(
        selectedDispatchers.map(username => usernameToDisplayname.get(username) || username)
      );
      filtered = filtered.filter(group => selectedDisplaynames.has(group.dispatcher));
    }

    if (selectedRegions.length > 0 || selectedDriverType.length > 0 || Object.values(columnSearches).some(v => v) || statusFilter.length > 0 || livePrefFilter.length > 0 || endorsementsFilter.length > 0) {
      filtered = filtered.map(group => ({
        ...group,
        drivers: group.drivers.filter(driver => {
          // Check driver column search
          if (columnSearches.driver) {
            const query = columnSearches.driver.toLowerCase();
            const matchesDriver = driver.driver.toLowerCase().includes(query);
            const matchesName = driver.driver_name?.toLowerCase().includes(query);
            const matchesParkingLoc = driver.parking_location_name?.toLowerCase().includes(query);
            if (!matchesDriver && !matchesName && !matchesParkingLoc) {
              return false;
            }
          }

          // Check driver type filter — matches the driver's LOCAL/OTR preference
          // badge, same as the Working tab. Truck type (Daycab/Sleeper) is a
          // separate concern handled by the Quals filter.
          if (!matchesDriverPreference(driver.preference, selectedDriverType)) {
            return false;
          }

          // Check endorsements filter — driver must hold ALL selected ones.
          // Whole-label match: this used to substring-match the joined string.
          if (!hasAllQualifications(driver.endorsements, endorsementsFilter)) {
            return false;
          }

          // Check start time filter
          if (columnSearches.startTime) {
            const query = columnSearches.startTime.toLowerCase().trim();
            const formattedTime = formatTime12Hour(driver.start_time).toLowerCase();
            if (!formattedTime.includes(query)) {
              return false;
            }
          }

          // Check live pref filter
          if (livePrefFilter.length > 0) {
            const driverLivePref = driver.live_pref || 'neutral';
            if (!livePrefFilter.includes(driverLivePref)) {
              return false;
            }
          }

          // Check status filter
          if (statusFilter.length > 0) {
            const dayStatus = driver.driver_status;
            const workingCategory = driver.working_category;
            // Check if any selected filter matches the status
            // Handle OOS variants: "Planned" should match "Planned (OOS)", etc.
            const workingCategoryValues = WORKING_CATEGORIES.map(c => c.value);
            const matches = statusFilter.some(filterStatus => {
              // Special case: Available filter - only match if no working category
              if (filterStatus === 'Available') {
                return dayStatus === 'Available' && (!workingCategory || workingCategory === '');
              }
              // Working-category filters: Available + matching category
              if (workingCategoryValues.includes(filterStatus)) {
                return dayStatus === 'Available' && workingCategory === filterStatus;
              }
              if (dayStatus === filterStatus) return true; // Exact match
              // Check if dayStatus is an OOS variant of the filter
              if (dayStatus === `${filterStatus} (OOS)`) return true;
              return false;
            });
            if (!matches) {
              return false;
            }
          }

          // Check preferred column search
          if (columnSearches.preferred) {
            const query = columnSearches.preferred.toLowerCase();
            const hasMatch = driver.preferred.some(loc =>
              loc.location_name.toLowerCase().includes(query)
            );
            if (!hasMatch) {
              return false;
            }
          }

          // Check not-preferred column search
          if (columnSearches.notPreferred) {
            const query = columnSearches.notPreferred.toLowerCase();
            const hasMatch = driver.not_preferred.some(loc =>
              loc.location_name.toLowerCase().includes(query)
            );
            if (!hasMatch) {
              return false;
            }
          }

          // Check banned column search
          if (columnSearches.banned) {
            const query = columnSearches.banned.toLowerCase();
            const hasMatch = driver.banned.some(loc =>
              loc.location_name.toLowerCase().includes(query)
            );
            if (!hasMatch) {
              return false;
            }
          }

          // Check delivery counts column search
          if (columnSearches.deliveryCounts) {
            const query = columnSearches.deliveryCounts.toLowerCase();
            const hasMatch = driver.delivery_counts?.some(delivery =>
              delivery.location_name.toLowerCase().includes(query)
            );
            if (!hasMatch) {
              return false;
            }
          }

          // Check rejections filter
          if (columnSearches.rejections) {
            const query = columnSearches.rejections.toLowerCase();
            const driverRejections = rejectionsByDriver[driver.driver] || [];
            const hasMatch = driverRejections.some(rejection =>
              rejection.pronumber.toLowerCase().includes(query) ||
              rejection.orig_name?.toLowerCase().includes(query) ||
              rejection.dest_name?.toLowerCase().includes(query) ||
              rejection.rejectreason?.toLowerCase().includes(query)
            );
            if (!hasMatch) {
              return false;
            }
          }

          // Check est pay filter (supports range like "100-400")
          if (columnSearches.estPay) {
            const query = columnSearches.estPay.trim();
            const estPay = driver.est_pay;

            // Check if it's a range filter (e.g., "100-400")
            const rangeMatch = query.match(/^(\d+)\s*-\s*(\d+)$/);
            if (rangeMatch) {
              const min = parseFloat(rangeMatch[1]);
              const max = parseFloat(rangeMatch[2]);
              if (estPay === null || estPay === undefined || estPay < min || estPay > max) {
                return false;
              }
            } else {
              // Fall back to string matching
              const estPayStr = estPay?.toString() || '';
              if (!estPayStr.includes(query)) {
                return false;
              }
            }
          }

          // Check est miles filter (supports range like "100-400")
          if (columnSearches.estMiles) {
            const query = columnSearches.estMiles.trim();
            const estMiles = driver.est_miles;

            // Check if it's a range filter (e.g., "100-400")
            const rangeMatch = query.match(/^(\d+)\s*-\s*(\d+)$/);
            if (rangeMatch) {
              const min = parseFloat(rangeMatch[1]);
              const max = parseFloat(rangeMatch[2]);
              if (estMiles === null || estMiles === undefined || estMiles < min || estMiles > max) {
                return false;
              }
            } else {
              // Fall back to string matching
              const estMilesStr = estMiles?.toString() || '';
              if (!estMilesStr.includes(query)) {
                return false;
              }
            }
          }

          // Check region filter
          if (selectedRegions.length > 0 && !selectedRegions.includes(driver.region)) {
            return false;
          }

          return true;
        })
      })).filter(group => group.drivers.length > 0);
    }

    // Apply sorting within each group
    if (sortState.column && sortState.direction) {
      // Column-based sorting takes precedence
      const sortDrivers = (drivers: DriverPlanningData[]) => {
        return [...drivers].sort((a, b) => {
          const dir = sortState.direction === 'asc' ? 1 : -1;

          switch (sortState.column) {
            case 'driver':
              return dir * ((parseInt(a.driver, 10) || 0) - (parseInt(b.driver, 10) || 0));
            case 'endorsements':
              return dir * (a.endorsements || '').localeCompare(b.endorsements || '');
            case 'driver_status':
              return dir * (a.driver_status || '').localeCompare(b.driver_status || '');
            case 'start_time':
              return dir * (a.start_time || '').localeCompare(b.start_time || '');
            case 'live_pref':
              return dir * (a.live_pref || 'neutral').localeCompare(b.live_pref || 'neutral');
            case 'preferred':
              return dir * (a.preferred.length - b.preferred.length);
            case 'not_preferred':
              return dir * (a.not_preferred.length - b.not_preferred.length);
            case 'banned':
              return dir * (a.banned.length - b.banned.length);
            case 'delivery_counts':
              const aTotal = a.delivery_counts?.reduce((sum, d) => sum + d.count, 0) || 0;
              const bTotal = b.delivery_counts?.reduce((sum, d) => sum + d.count, 0) || 0;
              return dir * (aTotal - bTotal);
            case 'est_pay':
              return dir * ((a.est_pay || 0) - (b.est_pay || 0));
            case 'est_miles':
              return dir * ((a.est_miles || 0) - (b.est_miles || 0));
            default:
              return 0;
          }
        });
      };

      filtered = filtered.map(group => ({
        ...group,
        drivers: sortDrivers(group.drivers)
      }));
    } else {
      // Apply saved driver sort order as default
      filtered = filtered.map(group => ({
        ...group,
        drivers: sortDriversByOrder(group.drivers, driverSortOrder[group.dispatcher])
      }));
    }

    return filtered;
  }, [data, selectedDispatchers, selectedRegions, selectedDriverType, columnSearches, statusFilter, livePrefFilter, endorsementsFilter, sortState, rejectionsByDriver, driverSortOrder, usernameToDisplayname]);

  // Flattened data for non-grouped view with sorting
  const flattenedData = useMemo(() => {
    const flat: FlatDriverData[] = [];
    filteredData.forEach(group => {
      group.drivers.forEach(driver => {
        flat.push({
          ...driver,
          dispatcher: group.dispatcher,
          dispatcher_region: group.region
        });
      });
    });

    // Apply sorting
    if (sortState.column && sortState.direction) {
      flat.sort((a, b) => {
        const dir = sortState.direction === 'asc' ? 1 : -1;

        switch (sortState.column) {
          case 'dispatcher':
            return dir * a.dispatcher.localeCompare(b.dispatcher);
          case 'driver':
            return dir * ((parseInt(a.driver, 10) || 0) - (parseInt(b.driver, 10) || 0));
          case 'endorsements':
            return dir * (a.endorsements || '').localeCompare(b.endorsements || '');
          case 'driver_status':
            return dir * (a.driver_status || '').localeCompare(b.driver_status || '');
          case 'start_time':
            return dir * (a.start_time || '').localeCompare(b.start_time || '');
          case 'live_pref':
            return dir * (a.live_pref || 'neutral').localeCompare(b.live_pref || 'neutral');
          case 'preferred':
            return dir * (a.preferred.length - b.preferred.length);
          case 'not_preferred':
            return dir * (a.not_preferred.length - b.not_preferred.length);
          case 'banned':
            return dir * (a.banned.length - b.banned.length);
          case 'delivery_counts':
            const aTotal = a.delivery_counts?.reduce((sum, d) => sum + d.count, 0) || 0;
            const bTotal = b.delivery_counts?.reduce((sum, d) => sum + d.count, 0) || 0;
            return dir * (aTotal - bTotal);
          case 'est_pay':
            return dir * ((a.est_pay || 0) - (b.est_pay || 0));
          case 'est_miles':
            return dir * ((a.est_miles || 0) - (b.est_miles || 0));
          default:
            return 0;
        }
      });
    }

    return flat;
  }, [filteredData, sortState]);

  // Handle column sort click
  const handleSort = (column: SortColumn) => {
    setSortState(prev => {
      if (prev.column !== column) {
        return { column, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      return { column: null, direction: null };
    });
  };

  // Sortable header component
  const SortableHeader: React.FC<{ column: SortColumn; label: string; className?: string; style?: React.CSSProperties }> = ({ column, label, className = '', style }) => {
    const thRef = useRef<HTMLTableCellElement>(null);
    const [width, setWidth] = useState<number>(0);

    useEffect(() => {
      if (!thRef.current) return;
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setWidth(Math.round(entry.contentRect.width));
        }
      });
      observer.observe(thRef.current);
      return () => observer.disconnect();
    }, []);

    return (
      <th
        ref={thRef}
        className={`px-1 py-0.5 text-left text-xs font-semibold border-r border-slate-500 cursor-pointer hover:bg-slate-500 transition-colors relative whitespace-nowrap ${className}`}
        style={style}
        onClick={() => handleSort(column)}
      >
        {showColumnWidths && <span className="absolute top-1 right-1 text-xs opacity-50">{width}px</span>}
        <div className="flex items-center gap-1">
          <span>{label}</span>
          <span className="text-slate-400">
            {sortState.column === column ? (
              sortState.direction === 'asc' ? '↑' : '↓'
            ) : (
              <span className="opacity-50">↕</span>
            )}
          </span>
        </div>
      </th>
    );
  };

  // Expand all dispatchers by default when data loads
  useEffect(() => {
    if (data.length > 0) {
      setExpandedDispatchers(new Set(data.map(g => g.dispatcher)));
    }
  }, [data]);

  // Fetch data when selected date changes or refresh triggered
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDate) {
        setData([]);
        setRejectionsByDriver({});
        setLoading(false);
        initialLoadCompleteRef.current = false;
        return;
      }

      // Only show loading on initial load, not on background refresh
      const isBackgroundRefresh = initialLoadCompleteRef.current;

      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      try {
        // Format date as YYYY-MM-DD
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        // Fetch planning data and rejections in parallel
        const [planningResponse, rejectionsResponse] = await Promise.all([
          api.get('/safety/driver-planning', {
            params: {
              date: dateStr,
              show_appointment_times: showAppointmentTimes,
              show_all_moves: planningShowAllMoves
            }
          }),
          api.get('/safety/driver-rejections', { params: { date: dateStr } })
        ]);

        setData(planningResponse.data || []);
        setRejectionsByDriver(rejectionsResponse.data || {});
        initialLoadCompleteRef.current = true;
      } catch (error) {
        console.error('Error fetching driver planning data:', error);
        if (!isBackgroundRefresh) {
          setData([]);
          setRejectionsByDriver({});
        }
      } finally {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [selectedDate, refreshTrigger, showAppointmentTimes, planningShowAllMoves]);

  // When only one of the Preferred / Non-Pref filters is in use, hide the other
  // list's badges so the rows show just the locations being searched for.
  const preferredFilterActive = columnSearches.preferred.trim() !== '';
  const notPreferredFilterActive = columnSearches.notPreferred.trim() !== '';
  const showPreferredBadges = preferredFilterActive || !notPreferredFilterActive;
  const showNotPreferredBadges = notPreferredFilterActive || !preferredFilterActive;

  if (!selectedDate) {
    return (
      <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto mb-16 flex items-center justify-center">
        <div className="text-center text-muted">
          <p className="text-lg">Please select a date to view driver planning</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto mb-16">
      <table className="w-full border-collapse text-sm">
        {/* Header */}
        <thead className="bg-slate-600 dark:bg-slate-700 text-white sticky top-0 z-10">
          <tr>
            {/* Driver/Dispatcher Header */}
            {compactTableMode ? (
              <th className="px-1 py-0.5 text-left text-xs font-semibold border-r border-slate-500 whitespace-nowrap w-64">
                <div className="flex flex-col">
                  <div
                    className="flex items-center gap-1 cursor-pointer hover:bg-slate-500 rounded px-1"
                    onClick={() => handleSort('driver')}
                  >
                    <span>Driver</span>
                    <span className="text-slate-400">
                      {sortState.column === 'driver' ? (
                        sortState.direction === 'asc' ? '↑' : '↓'
                      ) : (
                        <span className="opacity-50">↕</span>
                      )}
                    </span>
                  </div>
                  {!groupByDispatcher && (
                    <div
                      className="flex items-center gap-1 cursor-pointer hover:bg-slate-500 rounded px-1"
                      onClick={() => handleSort('dispatcher')}
                    >
                      <span>Dispatcher</span>
                      <span className="text-slate-400">
                        {sortState.column === 'dispatcher' ? (
                          sortState.direction === 'asc' ? '↑' : '↓'
                        ) : (
                          <span className="opacity-50">↕</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </th>
            ) : (
              <>
                {!groupByDispatcher && (
                  <SortableHeader column="dispatcher" label="Dispatcher" className="w-48" />
                )}
                <SortableHeader column="driver" label="Driver" className="w-64" />
              </>
            )}
            <SortableHeader column="endorsements" label="Quals" className="w-32 text-center" />
            <SortableHeader column="driver_status" label="Status" className="w-32 text-center" />
            {/* Start Time / Live Loads Header */}
            {compactTableMode ? (
              <th className="px-1 py-0.5 text-center text-xs font-semibold border-r border-slate-500 whitespace-nowrap">
                <div className="flex flex-col">
                  <div
                    className="flex items-center justify-center gap-1 cursor-pointer hover:bg-slate-500 rounded px-1"
                    onClick={() => handleSort('start_time')}
                  >
                    <span>Start</span>
                    <span className="text-slate-400">
                      {sortState.column === 'start_time' ? (
                        sortState.direction === 'asc' ? '↑' : '↓'
                      ) : (
                        <span className="opacity-50">↕</span>
                      )}
                    </span>
                  </div>
                  <div
                    className="flex items-center justify-center gap-1 cursor-pointer hover:bg-slate-500 rounded px-1"
                    onClick={() => handleSort('live_pref')}
                  >
                    <span>Live</span>
                    <span className="text-slate-400">
                      {sortState.column === 'live_pref' ? (
                        sortState.direction === 'asc' ? '↑' : '↓'
                      ) : (
                        <span className="opacity-50">↕</span>
                      )}
                    </span>
                  </div>
                </div>
              </th>
            ) : (
              <>
                <SortableHeader column="start_time" label="Start Time" className="w-32 text-center" />
                <SortableHeader column="live_pref" label="Live Loads" className="w-32 text-center" />
              </>
            )}
            {/* Location Pref / Non-Pref Header */}
            {compactTableMode ? (
              <th className="px-1 py-0.5 text-left text-xs font-semibold border-r border-slate-500 whitespace-nowrap">
                <div className="flex flex-col">
                  <div
                    className="flex items-center gap-1 cursor-pointer hover:bg-slate-500 rounded px-1"
                    onClick={() => handleSort('preferred')}
                  >
                    <span>Preferred</span>
                    <span className="text-slate-400">
                      {sortState.column === 'preferred' ? (
                        sortState.direction === 'asc' ? '↑' : '↓'
                      ) : (
                        <span className="opacity-50">↕</span>
                      )}
                    </span>
                  </div>
                  <div
                    className="flex items-center gap-1 cursor-pointer hover:bg-slate-500 rounded px-1"
                    onClick={() => handleSort('not_preferred')}
                  >
                    <span>Non-Pref</span>
                    <span className="text-slate-400">
                      {sortState.column === 'not_preferred' ? (
                        sortState.direction === 'asc' ? '↑' : '↓'
                      ) : (
                        <span className="opacity-50">↕</span>
                      )}
                    </span>
                  </div>
                </div>
              </th>
            ) : (
              <>
                <SortableHeader column="preferred" label="Location Pref" className="w-1/5" />
                <SortableHeader column="not_preferred" label="Location Non-Pref" className="w-1/5" />
              </>
            )}
            <SortableHeader column="banned" label="Bans" className="w-1/5" />
            <SortableHeader column="delivery_counts" label="Delivery Counts" style={{ minWidth: '300px' }} />
            <th className="px-1 py-0.5 text-left text-xs font-semibold whitespace-nowrap" style={{ minWidth: '200px' }}>Rejections</th>
            {/* Est Pay / Est Miles Header */}
            {compactTableMode ? (
              <th className="px-1 py-0.5 text-center text-xs font-semibold whitespace-nowrap">
                <div className="flex flex-col">
                  <div
                    className="flex items-center justify-center gap-1 cursor-pointer hover:bg-slate-500 rounded px-1"
                    onClick={() => handleSort('est_pay')}
                  >
                    <span>Pay</span>
                    <span className="text-slate-400">
                      {sortState.column === 'est_pay' ? (
                        sortState.direction === 'asc' ? '↑' : '↓'
                      ) : (
                        <span className="opacity-50">↕</span>
                      )}
                    </span>
                  </div>
                  <div
                    className="flex items-center justify-center gap-1 cursor-pointer hover:bg-slate-500 rounded px-1"
                    onClick={() => handleSort('est_miles')}
                  >
                    <span>Miles</span>
                    <span className="text-slate-400">
                      {sortState.column === 'est_miles' ? (
                        sortState.direction === 'asc' ? '↑' : '↓'
                      ) : (
                        <span className="opacity-50">↕</span>
                      )}
                    </span>
                  </div>
                </div>
              </th>
            ) : (
              <>
                <SortableHeader column="est_pay" label="Est Pay" className="w-32 text-center" />
                <SortableHeader column="est_miles" label="Est Miles" className="w-32 text-center" />
              </>
            )}
          </tr>
          {/* Filter Row */}
          <tr className="bg-slate-500">
            {/* Driver/Dispatcher Filter */}
            {compactTableMode ? (
              <th className="p-1 border-r border-slate-400">
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    value={columnSearches.driver}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, driver: e.target.value }))}
                    placeholder="Driver"
                    className="w-full px-2 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                  {!groupByDispatcher && (
                    <input
                      type="text"
                      value={columnSearches.dispatcher || ''}
                      onChange={(e) => setColumnSearches(prev => ({ ...prev, dispatcher: e.target.value }))}
                      placeholder="Dispatcher"
                      className="w-full px-2 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                    />
                  )}
                </div>
              </th>
            ) : (
              <>
                {!groupByDispatcher && (
                  <th className="p-1 border-r border-slate-400">
                    <input
                      type="text"
                      value={columnSearches.dispatcher || ''}
                      onChange={(e) => setColumnSearches(prev => ({ ...prev, dispatcher: e.target.value }))}
                      placeholder="Filter dispatcher"
                      className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                    />
                  </th>
                )}
                <th className="p-1 border-r border-slate-400">
                  <input
                    type="text"
                    value={columnSearches.driver}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, driver: e.target.value }))}
                    placeholder="Filter driver"
                    className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                </th>
              </>
            )}
            <th className="p-1 border-r border-slate-400">
              <MultiSelect
                options={endorsementsOptions}
                value={endorsementsFilter}
                onChange={setEndorsementsFilter}
                placeholder="All"
                showClearOption={true}
                clearOptionLabel="All"
                enableSearch={false}
                size="sm"
                className="w-full"
              />
            </th>
            <th className="p-1 border-r border-slate-400">
              <MultiSelect
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All"
                showClearOption={true}
                clearOptionLabel="All"
                enableSearch={false}
                size="sm"
                className="w-full"
              />
            </th>
            {/* Start Time / Live Filter */}
            {compactTableMode ? (
              <th className="p-1 border-r border-slate-400">
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    value={columnSearches.startTime}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, startTime: e.target.value }))}
                    placeholder="Start"
                    className="w-full px-2 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                  <MultiSelect
                    options={livePrefOptions}
                    value={livePrefFilter}
                    onChange={setLivePrefFilter}
                    placeholder="Live"
                    showClearOption={true}
                    clearOptionLabel="All"
                    enableSearch={false}
                    size="sm"
                    className="w-full"
                  />
                </div>
              </th>
            ) : (
              <>
                <th className="p-1 border-r border-slate-400">
                  <input
                    type="text"
                    value={columnSearches.startTime}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, startTime: e.target.value }))}
                    placeholder="Filter times"
                    className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                </th>
                <th className="p-1 border-r border-slate-400">
                  <MultiSelect
                    options={livePrefOptions}
                    value={livePrefFilter}
                    onChange={setLivePrefFilter}
                    placeholder="All"
                    showClearOption={true}
                    clearOptionLabel="All"
                    enableSearch={false}
                    size="sm"
                    className="w-full"
                  />
                </th>
              </>
            )}
            {/* Location Pref / Non-Pref Filter */}
            {compactTableMode ? (
              <th className="p-1 border-r border-slate-400">
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    value={columnSearches.preferred}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, preferred: e.target.value }))}
                    placeholder="Preferred"
                    className="w-full px-2 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                  <input
                    type="text"
                    value={columnSearches.notPreferred}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, notPreferred: e.target.value }))}
                    placeholder="Non-Pref"
                    className="w-full px-2 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                </div>
              </th>
            ) : (
              <>
                <th className="p-1 border-r border-slate-400">
                  <input
                    type="text"
                    value={columnSearches.preferred}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, preferred: e.target.value }))}
                    placeholder="Filter locations"
                    className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                </th>
                <th className="p-1 border-r border-slate-400">
                  <input
                    type="text"
                    value={columnSearches.notPreferred}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, notPreferred: e.target.value }))}
                    placeholder="Filter locations"
                    className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                </th>
              </>
            )}
            <th className="p-1 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.banned}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, banned: e.target.value }))}
                placeholder="Filter locations"
                className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
            <th className="p-1 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.deliveryCounts}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, deliveryCounts: e.target.value }))}
                placeholder="Filter counts"
                className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
            {/* Rejections Filter */}
            <th className="p-1 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.rejections}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, rejections: e.target.value }))}
                placeholder="Filter rejections"
                className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
              />
            </th>
            {/* Est Pay / Est Miles Filter */}
            {compactTableMode ? (
              <th className="p-1">
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    value={columnSearches.estPay}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, estPay: e.target.value }))}
                    placeholder="Pay"
                    className="w-full px-2 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                  <input
                    type="text"
                    value={columnSearches.estMiles}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, estMiles: e.target.value }))}
                    placeholder="Miles"
                    className="w-full px-2 py-0.5 text-xs border border-slate-400 rounded bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                </div>
              </th>
            ) : (
              <>
                <th className="p-1 border-r border-slate-400">
                  <input
                    type="text"
                    value={columnSearches.estPay}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, estPay: e.target.value }))}
                    placeholder="e.g. 100-400"
                    className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                </th>
                <th className="p-1">
                  <input
                    type="text"
                    value={columnSearches.estMiles}
                    onChange={(e) => setColumnSearches(prev => ({ ...prev, estMiles: e.target.value }))}
                    placeholder="e.g. 100-400"
                    className="w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600"
                  />
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="bg-panel">
          {loading ? (
            <>
              {/* Loading skeleton */}
              {[1, 2, 3].map((groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {/* Dispatcher skeleton row */}
                  <tr className="bg-slate-200 dark:bg-slate-700">
                    <td colSpan={11} className="p-1 border border-rule">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-slate-300 dark:bg-slate-600 rounded animate-pulse"></div>
                        <div className="h-4 w-32 bg-slate-300 dark:bg-slate-600 rounded animate-pulse"></div>
                      </div>
                    </td>
                  </tr>
                  {/* Driver skeleton rows */}
                  {[1, 2, 3].map((driverIndex) => (
                    <tr key={`${groupIndex}-${driverIndex}`}>
                      <td className="p-1 pl-4 border border-rule">
                        <div className="h-4 w-40 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                      <td className="p-1 border border-rule">
                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {/* Loading indicator overlay */}
              <tr>
                <td colSpan={11} className="p-4 text-center text-muted bg-white/80 dark:bg-gray-800/80">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-slate-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading driver planning...</span>
                  </div>
                </td>
              </tr>
            </>
          ) : (groupByDispatcher ? filteredData.length === 0 : flattenedData.length === 0) ? (
            <tr>
              <td colSpan={groupByDispatcher ? 11 : 12} className="p-8 text-center text-muted">
                No driver planning data for this date
              </td>
            </tr>
          ) : groupByDispatcher ? (
            // Grouped view
            filteredData.map((group) => {
              const isExpanded = expandedDispatchers.has(group.dispatcher);
              return (
                <React.Fragment key={group.dispatcher}>
                  {/* Dispatcher Row */}
                  <tr
                    className="bg-slate-200 dark:bg-slate-700 cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600"
                    onClick={() => toggleDispatcher(group.dispatcher)}
                  >
                    <td colSpan={11} className="px-1 py-0.5 font-semibold text-xs text-heading border border-rule">
                      <div className="flex items-center gap-1">
                        <ChevronRightIcon
                          className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        />
                        <span>{group.dispatcher}</span>
                        <span className="text-gray-500 font-normal text-[0.625rem]">
                          ({group.drivers.length})
                        </span>
                      </div>
                    </td>
                  </tr>
                  {/* Driver Rows */}
                  {isExpanded && group.drivers.map((driver) => (
                    <tr key={driver.driver} className="hover:bg-highlight">
                      <td className="px-1 py-0 pl-3 text-xs text-gray-700 dark:text-gray-200 border border-rule align-top whitespace-nowrap">
                        <div className="font-medium flex items-center gap-1 flex-wrap">
                          {driver.driver}<MuleBadge isMule={driver.isMule} />
                          {driver.driver_name && (
                            <span className="text-[0.625rem] text-gray-500 dark:text-white font-normal">{toTitleCase(driver.driver_name)}</span>
                          )}
                          <DriverPhoneLink textmsgnumber={driver.textmsgnumber} homephone={driver.homephone} />
                          {driver.preference && (
                            <span className={`text-[0.625rem] px-0.5 rounded ${
                              driver.preference === 'OTR'
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                : 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
                            }`}>
                              {driver.preference}
                            </span>
                          )}
                          {(() => {
                            const isDarkMode = document.documentElement.classList.contains('dark');
                            const colorStyles = getParkingColorStyles(driver.parking_color, isDarkMode);
                            return (
                              <button
                                onClick={() => openParkingModal(driver)}
                                className={`text-[0.625rem] px-1 py-0 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                                  !colorStyles && (driver.parking_location_name
                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                    : 'bg-gray-100 text-muted dark:bg-gray-700')
                                }`}
                                style={colorStyles || undefined}
                                title={driver.parking_location_name || 'Set parking location'}
                              >
                                {driver.parking_location_name || 'Parking'}
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-1 py-0 text-xs border border-rule align-top">
                        <div className="flex flex-wrap gap-0.5">
                          {parseQualifications(driver.endorsements)
                            .filter(showQual)
                            .map((consideration, idx) => (
                              <TagBadge
                                key={idx}
                                label={consideration}
                                color={getQualificationColor(consideration)}
                                className="whitespace-nowrap text-[0.625rem]"
                              />
                            ))}
                        </div>
                      </td>
                      <td className={`px-1 py-0 text-xs border border-gray-200 dark:border-gray-600 align-middle text-center whitespace-nowrap ${getStatusStyle(driver.driver_status, driver, driver.region)}`}>
                        {(() => {
                          const status = driver.driver_status;
                          const workingCategory = driver.working_category;
                          const hasComments = driver.status_comments?.trim() || driver.oos_comments?.trim() || driver.oos_type?.trim();
                          let displayContent;

                          const showCategory = showsPlannedCategoryLabel(workingCategory, showExtendedWorkingStatus);
                          if (status === 'Planned Accepted' && showCategory) {
                            displayContent = (
                              <div className="flex flex-col">
                                <div className="font-bold">Accepted</div>
                                <div className="text-xs">{getWorkingCategoryLabel(workingCategory)}</div>
                              </div>
                            );
                          } else if (status === 'Planned' && showCategory) {
                            displayContent = (
                              <div className="flex flex-col">
                                <div>Planned</div>
                                <div className="text-xs">{getWorkingCategoryLabel(workingCategory)}</div>
                              </div>
                            );
                          } else if (status === 'Available' && workingCategory) {
                            // Always show working category for Available status
                            displayContent = getWorkingCategoryLabel(workingCategory);
                          } else {
                            displayContent = getDisplayStatus(status) || '-';
                          }

                          return (
                            <div className="relative inline-flex items-center justify-center w-full">
                              {displayContent}
                              {hasComments && (
                                <Tooltip
                                  content={
                                    <div className="text-sm">
                                      {driver.oos_type && (
                                        <div className="font-medium mb-1">Type: {driver.oos_type}</div>
                                      )}
                                      {driver.status_comments?.trim() && (
                                        <div>{driver.status_comments}</div>
                                      )}
                                      {driver.oos_comments?.trim() && (
                                        <div>{driver.oos_comments}</div>
                                      )}
                                    </div>
                                  }
                                  placement="top"
                                >
                                  <ChatBubbleBottomCenterTextIcon className="w-3 h-3 absolute top-0 right-0 text-muted" />
                                </Tooltip>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      {/* Start Time / Live Cells */}
                      {compactTableMode ? (
                        <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                          <div className="flex flex-col items-center">
                            <span>{driver.start_time ? formatTime12Hour(driver.start_time) : '-'}</span>
                            {driver.live_pref && driver.live_pref !== 'neutral' && (
                              <TagBadge
                                label={driver.live_pref === 'yes' ? 'Yes' : 'No'}
                                color={driver.live_pref === 'yes' ? 'green' : 'red'}
                                className="whitespace-nowrap text-[0.625rem]"
                              />
                            )}
                          </div>
                        </td>
                      ) : (
                        <>
                          <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                            {driver.start_time ? formatTime12Hour(driver.start_time) : '-'}
                          </td>
                          <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                            {driver.live_pref && driver.live_pref !== 'neutral' ? (
                              <TagBadge
                                label={driver.live_pref === 'yes' ? 'Yes' : 'No'}
                                color={driver.live_pref === 'yes' ? 'green' : 'red'}
                                className="whitespace-nowrap text-[0.625rem]"
                              />
                            ) : '-'}
                          </td>
                        </>
                      )}
                      {/* Location Pref / Non-Pref Cells */}
                      {compactTableMode ? (
                        <td className="px-1 py-0 text-xs border border-rule align-top">
                          <div className="flex flex-col gap-0.5">
                            {showPreferredBadges && driver.preferred.length > 0 && (
                              <div className="flex flex-wrap gap-0.5">
                                {driver.preferred.map((loc, idx) => (
                                  <Tooltip
                                    key={idx}
                                    content={
                                      <div className="text-xs">
                                        {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                        <div className="font-medium">{loc.location_name}</div>
                                        {loc.location_address1 && (
                                          <div className="mt-1">
                                            <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                            <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                          </div>
                                        )}
                                      </div>
                                    }
                                    placement="top"
                                  >
                                    <TagBadge
                                      label={loc.location_name}
                                      color="green"
                                      size="sm"
                                      className="whitespace-nowrap text-[0.625rem]"
                                    />
                                  </Tooltip>
                                ))}
                              </div>
                            )}
                            {showNotPreferredBadges && driver.not_preferred.length > 0 && (
                              <div className="flex flex-wrap gap-0.5">
                                {driver.not_preferred.map((loc, idx) => (
                                  <Tooltip
                                    key={idx}
                                    content={
                                      <div className="text-xs">
                                        {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                        <div className="font-medium">{loc.location_name}</div>
                                        {loc.location_address1 && (
                                          <div className="mt-1">
                                            <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                            <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                          </div>
                                        )}
                                      </div>
                                    }
                                    placement="top"
                                  >
                                    <TagBadge
                                      label={loc.location_name}
                                      color="yellow"
                                      size="sm"
                                      className="whitespace-nowrap text-[0.625rem]"
                                    />
                                  </Tooltip>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      ) : (
                        <>
                          <td className="px-1 py-0 text-xs border border-rule align-top">
                            <div className="flex flex-wrap gap-0.5">
                              {showPreferredBadges && driver.preferred.map((loc, idx) => (
                                <Tooltip
                                  key={idx}
                                  content={
                                    <div className="text-xs">
                                      {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                      <div className="font-medium">{loc.location_name}</div>
                                      {loc.location_address1 && (
                                        <div className="mt-1">
                                          <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                          <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                        </div>
                                      )}
                                    </div>
                                  }
                                  placement="top"
                                >
                                  <TagBadge
                                    label={loc.location_name}
                                    color="green"
                                    size="sm"
                                    className="whitespace-nowrap text-[0.625rem]"
                                  />
                                </Tooltip>
                              ))}
                            </div>
                          </td>
                          <td className="px-1 py-0 text-xs border border-rule align-top">
                            <div className="flex flex-wrap gap-0.5">
                              {showNotPreferredBadges && driver.not_preferred.map((loc, idx) => (
                                <Tooltip
                                  key={idx}
                                  content={
                                    <div className="text-xs">
                                      {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                      <div className="font-medium">{loc.location_name}</div>
                                      {loc.location_address1 && (
                                        <div className="mt-1">
                                          <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                          <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                        </div>
                                      )}
                                    </div>
                                  }
                                  placement="top"
                                >
                                  <TagBadge
                                    label={loc.location_name}
                                    color="yellow"
                                    size="sm"
                                    className="whitespace-nowrap text-[0.625rem]"
                                  />
                                </Tooltip>
                              ))}
                            </div>
                          </td>
                        </>
                      )}
                      <td className="px-1 py-0 text-xs border border-rule align-top">
                        <div className="flex flex-wrap gap-0.5">
                          {driver.banned.map((loc, idx) => (
                            <Tooltip
                              key={idx}
                              content={
                                <div className="text-xs">
                                  {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                  <div className="font-medium">{loc.location_name}</div>
                                  {loc.location_address1 && (
                                    <div className="mt-1">
                                      <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                      <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                    </div>
                                  )}
                                  <div className="mt-1 font-medium text-red-400">
                                    {formatBanPeriod(loc)}
                                  </div>
                                </div>
                              }
                              placement="top"
                            >
                              <TagBadge
                                label={`${loc.location_name} - ${formatBanPeriod(loc)}`}
                                color="red"
                                size="sm"
                                className="whitespace-nowrap text-[0.625rem]"
                              />
                            </Tooltip>
                          ))}
                        </div>
                      </td>
                      {renderDeliveryCell(driver)}
                      {/* Rejections Cell */}
                      <td className="px-1 py-0 text-xs border border-rule align-top">
                        <div className="flex flex-wrap gap-0.5">
                          {rejectionsByDriver[driver.driver]?.map((rejection, idx) => (
                            <Tooltip
                              key={`rej-${idx}`}
                              content={
                                <div className="text-xs">
                                  <div className="font-medium text-red-600 mb-1">Rejected</div>
                                  <div><span className="font-medium">Reason:</span> {rejection.rejectreason || 'N/A'}</div>
                                  {rejection.rejectcomment && (
                                    <div><span className="font-medium">Comment:</span> {rejection.rejectcomment}</div>
                                  )}
                                  <div><span className="font-medium">PRO:</span> {rejection.pronumber}</div>
                                  <div><span className="font-medium">Origin:</span> {rejection.orig_name || rejection.origcode}</div>
                                  <div><span className="font-medium">Dest:</span> {rejection.dest_name || rejection.destcode}</div>
                                </div>
                              }
                              placement="top"
                            >
                              <TagBadge
                                label={`${rejection.pronumber} (${rejection.orig_name || rejection.origcode}→${rejection.dest_name || rejection.destcode})`}
                                color="red"
                                className="whitespace-nowrap text-[0.625rem]"
                              />
                            </Tooltip>
                          ))}
                        </div>
                      </td>
                      {/* Est Pay / Est Miles Cells */}
                      {compactTableMode ? (
                        <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                          <div className="flex flex-col">
                            <span>{driver.est_pay ? `$${driver.est_pay.toFixed(0)}` : '-'}</span>
                            <span className="text-gray-500">{driver.est_miles ? `${driver.est_miles.toFixed(0)} mi` : '-'}</span>
                          </div>
                        </td>
                      ) : (
                        <>
                          <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                            {driver.est_pay ? `$${driver.est_pay.toFixed(2)}` : '-'}
                          </td>
                          <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                            {driver.est_miles ? driver.est_miles.toFixed(0) : '-'}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })
          ) : (
            // Flat view (not grouped)
            flattenedData
              .filter(driver => {
                // Apply dispatcher column filter
                if (columnSearches.dispatcher) {
                  const query = columnSearches.dispatcher.toLowerCase();
                  if (!driver.dispatcher.toLowerCase().includes(query)) {
                    return false;
                  }
                }
                return true;
              })
              .map((driver) => (
              <tr key={`${driver.dispatcher}-${driver.driver}`} className="hover:bg-highlight">
                {/* Driver/Dispatcher Cell */}
                {compactTableMode ? (
                  <td className="px-1 py-0 text-xs text-gray-700 dark:text-gray-200 border border-rule align-top">
                    <div className="font-medium flex items-center gap-1 flex-wrap">
                      {driver.driver}<MuleBadge isMule={driver.isMule} />
                      {driver.driver_name && (
                        <span className="text-[0.625rem] text-gray-500 dark:text-white font-normal">{toTitleCase(driver.driver_name)}</span>
                      )}
                      <DriverPhoneLink textmsgnumber={driver.textmsgnumber} homephone={driver.homephone} />
                      {driver.preference && (
                        <span className={`text-[0.625rem] px-0.5 rounded ${
                          driver.preference === 'OTR'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                            : 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
                        }`}>
                          {driver.preference}
                        </span>
                      )}
                      {(() => {
                        const isDarkMode = document.documentElement.classList.contains('dark');
                        const colorStyles = getParkingColorStyles(driver.parking_color, isDarkMode);
                        return (
                          <button
                            onClick={() => openParkingModal(driver)}
                            className={`text-[0.625rem] px-1 py-0 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                              !colorStyles && (driver.parking_location_name
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                : 'bg-gray-100 text-muted dark:bg-gray-700')
                            }`}
                            style={colorStyles || undefined}
                            title={driver.parking_location_name || 'Set parking location'}
                          >
                            {driver.parking_location_name || 'Parking'}
                          </button>
                        );
                      })()}
                    </div>
                    <div className="text-[0.625rem] text-gray-400">{driver.dispatcher}</div>
                  </td>
                ) : (
                  <>
                    <td className="px-1 py-0 text-xs text-gray-700 dark:text-gray-200 border border-rule align-top">
                      {driver.dispatcher}
                    </td>
                    <td className="px-1 py-0 text-xs text-gray-700 dark:text-gray-200 border border-rule align-top">
                      <div className="font-medium flex items-center gap-1 flex-wrap">
                        {driver.driver}<MuleBadge isMule={driver.isMule} />
                        {driver.driver_name && (
                          <span className="text-[0.625rem] text-gray-500 dark:text-white font-normal">{toTitleCase(driver.driver_name)}</span>
                        )}
                        <DriverPhoneLink textmsgnumber={driver.textmsgnumber} homephone={driver.homephone} />
                        {driver.preference && (
                          <span className={`text-[0.625rem] px-0.5 rounded ${
                            driver.preference === 'OTR'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                              : 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
                          }`}>
                            {driver.preference}
                          </span>
                        )}
                        {(() => {
                          const isDarkMode = document.documentElement.classList.contains('dark');
                          const colorStyles = getParkingColorStyles(driver.parking_color, isDarkMode);
                          return (
                            <button
                              onClick={() => openParkingModal(driver)}
                              className={`text-[0.625rem] px-1 py-0 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                                !colorStyles && (driver.parking_location_name
                                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                                  : 'bg-gray-100 text-muted dark:bg-gray-700')
                              }`}
                              style={colorStyles || undefined}
                              title={driver.parking_location_name || 'Set parking location'}
                            >
                              {driver.parking_location_name || 'Parking'}
                            </button>
                          );
                        })()}
                      </div>
                    </td>
                  </>
                )}
                <td className="px-1 py-0 text-xs border border-rule align-top">
                  <div className="flex flex-wrap gap-0.5">
                    {parseQualifications(driver.endorsements)
                      .filter(showQual)
                      .map((consideration, idx) => (
                        <TagBadge
                          key={idx}
                          label={consideration}
                          color={getQualificationColor(consideration)}
                          className="whitespace-nowrap text-[0.625rem]"
                        />
                      ))}
                  </div>
                </td>
                <td className={`px-1 py-0 text-xs border border-gray-200 dark:border-gray-600 align-middle text-center ${getStatusStyle(driver.driver_status, driver, driver.region)}`}>
                  {(() => {
                    const status = driver.driver_status;
                    const workingCategory = driver.working_category;
                    let displayContent;

                    const showCategory = showsPlannedCategoryLabel(workingCategory, showExtendedWorkingStatus);
                    if (status === 'Planned Accepted' && showCategory) {
                      displayContent = (
                        <div className="flex flex-col">
                          <div className="font-bold">Accepted</div>
                          <div className="text-xs">{getWorkingCategoryLabel(workingCategory)}</div>
                        </div>
                      );
                    } else if (status === 'Planned' && showCategory) {
                      displayContent = (
                        <div className="flex flex-col">
                          <div>Planned</div>
                          <div className="text-xs">{getWorkingCategoryLabel(workingCategory)}</div>
                        </div>
                      );
                    } else if (status === 'Available' && workingCategory) {
                      displayContent = getWorkingCategoryLabel(workingCategory);
                    } else if (status === 'Out Of Service' && (driver.oos_comments?.trim() || driver.oos_type?.trim())) {
                      displayContent = (
                        <Tooltip
                          content={
                            <div className="text-sm">
                              {driver.oos_type && (
                                <div className="font-medium mb-1">Type: {driver.oos_type}</div>
                              )}
                              {driver.oos_comments?.trim() && (
                                <div>{driver.oos_comments}</div>
                              )}
                            </div>
                          }
                          placement="top"
                        >
                          <span>{getDisplayStatus(status)}</span>
                        </Tooltip>
                      );
                    } else {
                      displayContent = getDisplayStatus(status) || '-';
                    }

                    return displayContent;
                  })()}
                </td>
                {/* Start Time / Live Cells */}
                {compactTableMode ? (
                  <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                    <div className="flex flex-col items-center">
                      <span>{driver.start_time ? formatTime12Hour(driver.start_time) : '-'}</span>
                      {driver.live_pref && driver.live_pref !== 'neutral' && (
                        <TagBadge
                          label={driver.live_pref === 'yes' ? 'Yes' : 'No'}
                          color={driver.live_pref === 'yes' ? 'green' : 'red'}
                          className="whitespace-nowrap text-[0.625rem]"
                        />
                      )}
                    </div>
                  </td>
                ) : (
                  <>
                    <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                      {driver.start_time ? formatTime12Hour(driver.start_time) : '-'}
                    </td>
                    <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                      {driver.live_pref && driver.live_pref !== 'neutral' ? (
                        <TagBadge
                          label={driver.live_pref === 'yes' ? 'Yes' : 'No'}
                          color={driver.live_pref === 'yes' ? 'green' : 'red'}
                          className="whitespace-nowrap text-[0.625rem]"
                        />
                      ) : '-'}
                    </td>
                  </>
                )}
                {/* Location Pref / Non-Pref Cells */}
                {compactTableMode ? (
                  <td className="px-1 py-0 text-xs border border-rule align-top">
                    <div className="flex flex-col gap-0.5">
                      {showPreferredBadges && driver.preferred.length > 0 && (
                        <div className="flex flex-wrap gap-0.5">
                          {driver.preferred.map((loc, idx) => (
                            <Tooltip
                              key={idx}
                              content={
                                <div className="text-xs">
                                  {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                  <div className="font-medium">{loc.location_name}</div>
                                  {loc.location_address1 && (
                                    <div className="mt-1">
                                      <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                      <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                    </div>
                                  )}
                                </div>
                              }
                              placement="top"
                            >
                              <TagBadge
                                label={loc.location_name}
                                color="green"
                                size="sm"
                                className="whitespace-nowrap text-[0.625rem]"
                              />
                            </Tooltip>
                          ))}
                        </div>
                      )}
                      {showNotPreferredBadges && driver.not_preferred.length > 0 && (
                        <div className="flex flex-wrap gap-0.5">
                          {driver.not_preferred.map((loc, idx) => (
                            <Tooltip
                              key={idx}
                              content={
                                <div className="text-xs">
                                  {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                  <div className="font-medium">{loc.location_name}</div>
                                  {loc.location_address1 && (
                                    <div className="mt-1">
                                      <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                      <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                    </div>
                                  )}
                                </div>
                              }
                              placement="top"
                            >
                              <TagBadge
                                label={loc.location_name}
                                color="yellow"
                                size="sm"
                                className="whitespace-nowrap text-[0.625rem]"
                              />
                            </Tooltip>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                ) : (
                  <>
                    <td className="px-1 py-0 text-xs border border-rule align-top">
                      <div className="flex flex-wrap gap-0.5">
                        {showPreferredBadges && driver.preferred.map((loc, idx) => (
                          <Tooltip
                            key={idx}
                            content={
                              <div className="text-xs">
                                {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                <div className="font-medium">{loc.location_name}</div>
                                {loc.location_address1 && (
                                  <div className="mt-1">
                                    <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                    <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                  </div>
                                )}
                              </div>
                            }
                            placement="top"
                          >
                            <TagBadge
                              label={loc.location_name}
                              color="green"
                              size="sm"
                              className="whitespace-nowrap text-[0.625rem]"
                            />
                          </Tooltip>
                        ))}
                      </div>
                    </td>
                    <td className="px-1 py-0 text-xs border border-rule align-top">
                      <div className="flex flex-wrap gap-0.5">
                        {showNotPreferredBadges && driver.not_preferred.map((loc, idx) => (
                          <Tooltip
                            key={idx}
                            content={
                              <div className="text-xs">
                                {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                                <div className="font-medium">{loc.location_name}</div>
                                {loc.location_address1 && (
                                  <div className="mt-1">
                                    <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                    <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                                  </div>
                                )}
                              </div>
                            }
                            placement="top"
                          >
                            <TagBadge
                              label={loc.location_name}
                              color="yellow"
                              size="sm"
                              className="whitespace-nowrap text-[0.625rem]"
                            />
                          </Tooltip>
                        ))}
                      </div>
                    </td>
                  </>
                )}
                <td className="px-1 py-0 text-xs border border-rule align-top">
                  <div className="flex flex-wrap gap-0.5">
                    {driver.banned.map((loc, idx) => (
                      <Tooltip
                        key={idx}
                        content={
                          <div className="text-xs">
                            {loc.location_type && <div className="font-medium mb-1">{loc.location_type}</div>}
                            <div className="font-medium">{loc.location_name}</div>
                            {loc.location_address1 && (
                              <div className="mt-1">
                                <div>{[loc.location_address1, loc.location_address2].filter(Boolean).join(' ')}</div>
                                <div>{loc.location_city}, {loc.location_state} {loc.location_zip}</div>
                              </div>
                            )}
                            <div className="mt-1 font-medium text-red-400">
                              {formatBanPeriod(loc)}
                            </div>
                          </div>
                        }
                        placement="top"
                      >
                        <TagBadge
                          label={`${loc.location_name} - ${formatBanPeriod(loc)}`}
                          color="red"
                          size="sm"
                          className="whitespace-nowrap text-[0.625rem]"
                        />
                      </Tooltip>
                    ))}
                  </div>
                </td>
                {renderDeliveryCell(driver)}
                {/* Rejections Cell */}
                <td className="px-1 py-0 text-xs border border-rule align-top">
                  <div className="flex flex-wrap gap-0.5">
                    {rejectionsByDriver[driver.driver]?.map((rejection, idx) => (
                      <Tooltip
                        key={`rej-${idx}`}
                        content={
                          <div className="text-xs">
                            <div className="font-medium text-red-600 mb-1">Rejected</div>
                            <div><span className="font-medium">Reason:</span> {rejection.rejectreason || 'N/A'}</div>
                            {rejection.rejectcomment && (
                              <div><span className="font-medium">Comment:</span> {rejection.rejectcomment}</div>
                            )}
                            <div><span className="font-medium">PRO:</span> {rejection.pronumber}</div>
                            <div><span className="font-medium">Origin:</span> {rejection.orig_name || rejection.origcode}</div>
                            <div><span className="font-medium">Dest:</span> {rejection.dest_name || rejection.destcode}</div>
                          </div>
                        }
                        placement="top"
                      >
                        <TagBadge
                          label={`${rejection.pronumber} (${rejection.orig_name || rejection.origcode}→${rejection.dest_name || rejection.destcode})`}
                          color="red"
                          className="whitespace-nowrap text-[0.625rem]"
                        />
                      </Tooltip>
                    ))}
                  </div>
                </td>
                {/* Est Pay / Est Miles Cells */}
                {compactTableMode ? (
                  <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                    <div className="flex flex-col">
                      <span>{driver.est_pay ? `$${driver.est_pay.toFixed(0)}` : '-'}</span>
                      <span className="text-gray-500">{driver.est_miles ? `${driver.est_miles.toFixed(0)} mi` : '-'}</span>
                    </div>
                  </td>
                ) : (
                  <>
                    <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                      {driver.est_pay ? `$${driver.est_pay.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-1 py-0 text-xs border border-rule align-top text-center">
                      {driver.est_miles ? driver.est_miles.toFixed(0) : '-'}
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Parking Location Modal */}
      <ParkingLocationModal
        isOpen={parkingModalOpen}
        onClose={() => setParkingModalOpen(false)}
        driver={parkingModalDriver}
        driverName={parkingModalDriverName}
        currentParkingloc={parkingModalCurrentLoc}
        currentParkingName={parkingModalCurrentName}
        currentParkingColor={parkingModalCurrentColor}
        onSave={handleParkingLocationSave}
      />
    </div>
  );
};

export default DriverPlanning;
